import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { useProfitTrends } from "@/context/ProfitTrendsContext";
import { formatCurrency } from "@/utils/formatCurrency";
import { ResponsiveContainer, AreaChart, Area, Tooltip, XAxis } from "recharts";

export const DailyProfitMetrics = (): React.ReactNode => {
  const { profitTrends, loading } = useProfitTrends();

  if (loading || !profitTrends) {
    return (
      <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
        <CardHeader className="pb-2">
          <CardDescription>Last 24 Hours Profit</CardDescription>
          <CardTitle className="text-4xl">Loading...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const last24Hours = profitTrends.last24Hours ?? profitTrends.today;
  const todayProfit = last24Hours.profit;
  const weekProfit = profitTrends.thisWeek.profit;
  const todayColor = todayProfit >= 0 ? "text-green-600" : "text-red-600";

  // Get this week's daily data (last 7 days)
  const weeklyData = profitTrends.daily.slice(-7).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", { weekday: "short" }),
    profit: d.profit,
  }));

  const hasProfit = weeklyData.some((d) => d.profit !== 0);
  const chartColor = weekProfit >= 0 ? "#16a34a" : "#dc2626";

  return (
    <Card x-chunk="dashboard-05-chunk-1" className="flex flex-col h-full">
      <CardHeader className="pb-2">
        <CardDescription>Last 24 Hours Profit</CardDescription>
        <CardTitle className={`text-4xl ${todayColor}`}>
          {formatCurrency(todayProfit, { showSign: todayProfit > 0 })}
        </CardTitle>
      </CardHeader>
      <div className="mt-auto">
        <CardContent className="pb-2">
          <p className="text-xs text-muted-foreground">
            {formatCurrency(weekProfit, { showSign: weekProfit > 0 })} this trading week
            ({profitTrends.thisWeek.trades} trades)
          </p>
        </CardContent>
        {hasProfit && (
          <div className="px-2 pb-3">
            <ResponsiveContainer width="100%" height={60}>
              <AreaChart data={weeklyData}>
                <defs>
                  <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColor} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(Number(value)), "Profit"]}
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  stroke={chartColor}
                  strokeWidth={2}
                  fill="url(#profitGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Card>
  );
};
